import { useEffect, useState } from "react";
import { toast } from "sonner";
import apiClient from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Check, Lightning, Crown, Sparkle } from "@phosphor-icons/react";

const FEATURES = [
  "Accuracy & error audit", "Profitability & margin analysis", "Labor allocation analysis",
  "Scope & risk analysis", "Forensics scoring", "Live regional market data", "Proposal history",
];

const META = {
  onetime: { tag: "ONE-TIME PURCHASE", sub: "1 proposal", cta: "Buy Single Proposal", accent: "slate" },
  starter: { sub: "15 proposals / month", cta: "Choose Starter", accent: "slate" },
  professional: { badge: "MOST POPULAR", sub: "40 proposals / month", cta: "Choose Professional", accent: "blue" },
  enterprise: { sub: "100 proposals / month", cta: "Choose Enterprise", accent: "slate" },
  annual: { badge: "BEST VALUE", sub: "Unlimited proposals all year", cta: "Choose Annual", accent: "gold" },
};

export default function PricingPage() {
  const { company } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");
  const currentPlan = company?.usage?.plan;

  useEffect(() => { apiClient.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  const checkout = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const r = await apiClient.post("/subscriptions/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout. Are you an admin?");
      setBusy("");
    }
  };

  return (
    <div className="space-y-10" data-testid="pricing-page">
      <div className="text-center max-w-2xl mx-auto">
        <div className="font-mono-plex text-[11px] uppercase tracking-[0.3em] text-blue-500 font-semibold">Plans &amp; Pricing</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-[#0F172A] mt-3">Keep finding the problems<br />before they become projects.</h1>
        <p className="text-slate-500 mt-4">Every plan includes the full Proposal Forensics analysis engine. Scale your monthly proposal volume as you grow.</p>
      </div>

      <div data-testid="founding-pricing-banner" className="max-w-3xl mx-auto rounded-xl border border-blue-200 bg-blue-50/70 px-5 py-4 text-center">
        <div className="inline-flex items-center gap-2 font-mono-plex text-[11px] uppercase tracking-[0.22em] text-blue-600 font-bold">
          <Sparkle size={14} weight="fill" /> Founding Customer Pricing
        </div>
        <p className="text-sm text-slate-600 mt-1.5">
          Available through <strong className="text-[#0F172A]">October 31, 2026</strong>. Regular pricing begins <strong className="text-[#0F172A]">November 1, 2026</strong>. Customers who subscribe during launch keep their introductory subscription price while active.
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-5 md:grid-cols-2">
        {plans.map((p) => {
          const m = META[p.id] || {};
          const isBlue = m.accent === "blue";
          const isGold = m.accent === "gold";
          const isCurrent = currentPlan === p.id;
          return (
            <div key={p.lookup_key} data-testid={`plan-card-${p.id}`}
              className={`relative rounded-xl border p-6 flex flex-col transition-all ${
                isBlue ? "border-blue-500 ring-2 ring-blue-500/30 bg-white shadow-lg lg:-mt-3 lg:mb-3"
                : isGold ? "border-transparent bg-black text-white grain shadow-xl"
                : "border-slate-200 bg-white"}`}>
              {m.badge && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider ${isGold ? "bg-blue-500 text-white" : "bg-blue-600 text-white"}`}>
                  {m.badge}
                </div>
              )}
              <div className="flex items-center gap-2">
                {p.id === "annual" && <Crown size={18} weight="fill" className="text-blue-400" />}
                {p.id === "professional" && <Lightning size={18} weight="fill" className="text-blue-600" />}
                <h3 className={`font-display font-bold text-lg ${isGold ? "text-white" : "text-[#0F172A]"}`}>{p.name}</h3>
              </div>
              {m.tag && <div className="font-mono-plex text-[9px] uppercase tracking-[0.2em] text-slate-400 mt-1">{m.tag}</div>}
              <div className="mt-4">
                {p.regular_price && (
                  <div className="flex items-baseline gap-2" data-testid={`plan-regular-price-${p.id}`}>
                    <span className={`text-sm line-through ${isGold ? "text-slate-500" : "text-slate-400"}`}>{p.regular_price}</span>
                    <span className={`font-mono-plex text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${isGold ? "bg-blue-500/20 text-blue-300" : "bg-blue-100 text-blue-700"}`}>Founding</span>
                  </div>
                )}
                <div className="mt-0.5">
                  <span data-testid={`plan-price-${p.id}`} className={`font-display font-black text-4xl tracking-tighter ${isGold ? "text-white" : "text-[#0F172A]"}`}>{p.price}</span>
                  <span className={`text-sm ${isGold ? "text-slate-400" : "text-slate-400"}`}>{p.interval === "month" ? "/mo" : p.interval === "year" ? "/yr" : ""}</span>
                </div>
              </div>
              <div className={`text-sm font-semibold mt-1 ${isGold ? "text-blue-300" : isBlue ? "text-blue-600" : "text-slate-600"}`}>{m.sub}</div>

              {p.id === "annual" && (
                <div className="mt-3 rounded-md bg-white/10 border border-white/15 p-3">
                  <div className="font-display font-bold text-emerald-400 text-sm">UNLIMITED PROPOSALS · SAVE $859.89 / YEAR</div>
                  <div className="text-[11px] text-slate-300 mt-0.5">≈ 55% vs Enterprise monthly ($1,559.88/yr)</div>
                </div>
              )}

              <ul className="mt-5 space-y-2 flex-1">
                {(p.id === "onetime" ? FEATURES.slice(0, 5) : FEATURES).map((f) => (
                  <li key={f} className={`flex items-start gap-2 text-xs ${isGold ? "text-slate-200" : "text-slate-600"}`}>
                    <Check size={14} weight="bold" className={isGold ? "text-blue-400 mt-0.5" : "text-blue-600 mt-0.5"} /> {f}
                  </li>
                ))}
              </ul>

              <button data-testid={`plan-cta-${p.id}`} disabled={busy === p.lookup_key || isCurrent} onClick={() => checkout(p.lookup_key)}
                className={`mt-6 h-11 rounded-md font-bold text-sm transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2 ${
                  isGold ? "bg-blue-500 hover:bg-blue-400 text-white"
                  : isBlue ? "bg-blue-600 hover:bg-blue-500 text-white"
                  : "bg-[#0F172A] hover:bg-[#1E293B] text-white"}`}>
                {isCurrent ? "Current Plan" : busy === p.lookup_key ? "Redirecting…" : <><Sparkle size={15} weight="fill" /> {m.cta}</>}
              </button>
            </div>
          );
        })}
      </div>
      <p className="text-center text-xs text-slate-400" data-testid="pricing-checkout-note">Secure checkout via Stripe · Live payments only.</p>
    </div>
  );
}
